import React, { useEffect, useState, useRef } from "react";
import { api } from "../../services/erpApi";

const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1px solid #cbd5e1", borderRadius: "6px",
  fontSize: "13.5px", outline: "none", background: "#fff", boxSizing: "border-box",
};

export default function CustomerSelect({ value, onChange, placeholder = "Select customer…", required }) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState("");
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Resolve selected label when value changes
  useEffect(() => {
    if (!value) { setSelectedLabel(""); setQuery(""); return; }
    const found = options.find(o => String(o.id) === String(value));
    if (found) {
      setSelectedLabel(found.display_name || found.name);
      setQuery(found.display_name || found.name);
      return;
    }
    let active = true;
    (async () => {
      try {
        const p = await api.partners.get(value);
        const d = p?.data ? p.data : p;
        if (active && d) {
          const label = d.display_name || d.name || `Partner #${value}`;
          setSelectedLabel(label);
          setQuery(label);
          // keep in options for display
          setOptions(prev => {
            if (prev.some(o => String(o.id) === String(d.id))) return prev;
            return [{ id: d.id, display_name: d.display_name, name: d.name, email: d.email }, ...prev];
          });
        }
      } catch {}
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Initial load: fetch first 20 customers
  useEffect(() => {
    fetchOptions("");
  }, []);

  const fetchOptions = async (q) => {
    setLoading(true);
    try {
      const params = { limit: 20, is_active: "true" };
      if (q && q.trim()) params.search = q.trim();
      // ROUTES.md expects customer_rank as int; use 1 to filter customers, but fallback shows all active if empty
      // Try with customer_rank=1 first, fallback to without if no results and q empty handled by backend
      let rows = await api.partners.list({ ...params, customer_rank: 1 }).catch(() => null);
      let arr = Array.isArray(rows) ? rows : rows?.data && Array.isArray(rows.data) ? rows.data : [];
      // If custom filter returns empty and we are not searching, fallback to all active (like AdminPanelPartnersPage does)
      if ((!arr || arr.length === 0) && !q) {
        rows = await api.partners.list(params).catch(() => []);
        arr = Array.isArray(rows) ? rows : rows?.data && Array.isArray(rows.data) ? rows.data : [];
        arr = arr.filter(p => Number(p.customer_rank) > 0 || p.customer === true);
        if (arr.length === 0) {
          rows = await api.partners.list({ limit: 20, is_active: "all" }).catch(() => []);
          arr = Array.isArray(rows) ? rows : rows?.data && Array.isArray(rows.data) ? rows.data : [];
        }
      }
      // When searching, if customer_rank filter gave 0, retry without it for search
      if ((!arr || arr.length === 0) && q) {
        rows = await api.partners.list(params).catch(() => []);
        arr = Array.isArray(rows) ? rows : rows?.data && Array.isArray(rows.data) ? rows.data : [];
      }
      setOptions(Array.isArray(arr) ? arr : []);
    } catch {
      setOptions([]);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    setOpen(true);
    // if user clears, notify parent
    if (!v) {
      onChange?.("");
      setSelectedLabel("");
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchOptions(v), 300);
  };

  const handleSelect = (p) => {
    const label = p.display_name || p.name;
    setSelectedLabel(label);
    setQuery(label);
    setOpen(false);
    onChange?.(String(p.id));
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
      <input
        value={open ? query : (selectedLabel || query)}
        onChange={handleInputChange}
        onFocus={() => { setOpen(true); if (!query) fetchOptions(""); }}
        placeholder={placeholder}
        style={inputStyle}
        autoComplete="off"
      />
      {required && !value && <span style={{ color: "#be123c", fontSize: 11 }}> *</span>}
      {open && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50,
          background: "#fff", border: "1px solid #cbd5e1", borderTop: 0, borderRadius: "0 0 6px 6px",
          maxHeight: 240, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.08)"
        }}>
          {loading && <div style={{ padding: "10px 12px", fontSize: 13, color: "#64748b" }}>Loading…</div>}
          {!loading && options.length === 0 && <div style={{ padding: "10px 12px", fontSize: 13, color: "#94a3b8" }}>No customers found</div>}
          {!loading && options.map(p => (
            <div
              key={p.id}
              onClick={() => handleSelect(p)}
              style={{
                padding: "8px 12px", cursor: "pointer", fontSize: 13,
                background: String(p.id) === String(value) ? "#f0f7f0" : "#fff",
                borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center"
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
              onMouseLeave={e => e.currentTarget.style.background = String(p.id) === String(value) ? "#f0f7f0" : "#fff"}
            >
              <span style={{ fontWeight: 500, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.display_name || p.name}</span>
              <span style={{ fontSize: 11, color: "#64748b", marginLeft: 8, whiteSpace: "nowrap" }}>{p.email || p.city || ""}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
